import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';
import { setTheme } from '@/store/appSlice';
import { Tooltip } from '@/components/ui/Tooltip';
import { getModuleEntryByErpModule } from '@/config/moduleEntries';
import { useWorkspaceTitle } from '@/hooks/useWorkspaceTitle';
import { CompanySwitcher } from '@/components/layout/CompanySwitcher';
import { ProfileMenu } from '@/components/layout/ProfileMenu';
import { PosStorePicker } from '@/components/pos/PosStorePicker';
import { PosStoreSelector } from '@/components/pos/PosStoreSelector';
import { PosCounterPicker } from '@/components/pos/PosCounterPicker';
import { PosHardwareProvider } from '@/pages/pos/PosHardwareContext';
import { PosSessionProvider } from '@/hooks/usePosSession';
import { KeyboardShortcutProvider } from '@/core/keyboard';
import type { ShortcutScope } from '@/core/keyboard/types';

const TERMINAL_NAV = [
  { path: '/pos', label: 'Billing' },
  { path: '/pos/held', label: 'Held' },
  { path: '/pos/shift', label: 'Shift' },
  { path: '/pos/returns', label: 'Returns' },
  { path: '/pos/analytics', label: 'Analytics' },
  { path: '/pos/setup', label: 'Setup' },
] as const;

function resolvePosShortcutScope(pathname: string): ShortcutScope {
  if (pathname === '/pos/held' || pathname.startsWith('/pos/held/')) return 'pos-held';
  return 'pos';
}

function PosLayoutShell() {
  useWorkspaceTitle();
  const posModuleLabel = getModuleEntryByErpModule('POS')?.label ?? 'Point of Sale';
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const selectedStore = useAppSelector((s) => s.app.selectedStore);
  const companyScopeVersion = useAppSelector((s) => s.app.companyScopeVersion);
  const user = useAppSelector((s) => s.auth.user);
  const theme = useAppSelector((s) => s.app.theme);
  const location = useLocation();
  const shortcutScope = resolvePosShortcutScope(location.pathname);

  return (
    <KeyboardShortcutProvider scope={shortcutScope}>
      <header className="workspace-header relative z-20 h-12 shrink-0 gap-2 px-3 sm:px-4">
        <div className="workspace-header-start min-w-0 flex-1">
          <button
            type="button"
            onClick={() => navigate(user?.masterAdmin ? '/master' : '/pos/dashboard')}
            className="flex shrink-0 items-center gap-1 text-sm text-slate-900 hover:text-primary-600 dark:text-white dark:hover:text-primary-400"
          >
            <ArrowBackIcon fontSize="small" />
            <span className="hidden sm:inline">Dashboard</span>
          </button>
          <span className="hidden truncate text-sm font-semibold text-slate-900 dark:text-slate-100 lg:inline">
            {posModuleLabel} terminal
          </span>
          <CompanySwitcher className="hidden min-w-[7rem] max-w-[10rem] md:block lg:min-w-[8rem] lg:max-w-[12rem]" />
          <PosStoreSelector className="min-w-[7rem] max-w-[10rem] sm:min-w-[8rem] sm:max-w-[12rem]" />
        </div>
        <nav className="hidden shrink-0 gap-0.5 text-sm xl:flex">
          {TERMINAL_NAV.map((item) => (
            <button
              key={item.path}
              type="button"
              className={`rounded-lg px-2 py-1.5 transition ${
                location.pathname === item.path
                  ? 'bg-primary-50 font-semibold text-primary-700 dark:bg-primary-950/50 dark:text-primary-300'
                  : 'text-slate-700 hover:bg-slate-100 hover:text-primary-700 dark:text-slate-200 dark:hover:bg-slate-800 dark:hover:text-primary-300'
              }`}
              onClick={() => navigate(item.path)}
            >
              {item.label}
            </button>
          ))}
        </nav>
        <div className="workspace-header-end shrink-0">
          {selectedStore && <PosCounterPicker />}
          <Tooltip content="Toggle theme">
            <button
              type="button"
              className="workspace-header-ghost-btn"
              onClick={() => dispatch(setTheme(theme === 'dark' ? 'light' : 'dark'))}
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
            </button>
          </Tooltip>
          <ProfileMenu variant="header" />
        </div>
      </header>
      <div className="flex-1 overflow-hidden">
        {selectedStore ? (
          <PosHardwareProvider key={`${companyScopeVersion}-${selectedStore.id}`}>
            <Outlet key={companyScopeVersion} />
          </PosHardwareProvider>
        ) : (
          <PosStorePicker />
        )}
      </div>
    </KeyboardShortcutProvider>
  );
}

export function PosLayout() {
  const selectedStore = useAppSelector((s) => s.app.selectedStore);
  const companyScopeVersion = useAppSelector((s) => s.app.companyScopeVersion);

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-page text-slate-900 dark:bg-slate-900 dark:text-slate-100">
      {selectedStore ? (
        <PosSessionProvider key={`${companyScopeVersion}-${selectedStore.id}`} storeId={selectedStore.id}>
          <PosLayoutShell />
        </PosSessionProvider>
      ) : (
        <PosLayoutShell />
      )}
    </div>
  );
}
